import axios, { type AxiosError, type AxiosRequestConfig, type AxiosResponseHeaders } from "axios";
import type { Project } from "@/api/projects";
import type { Laboratory } from "./laboratory";
import type { ProjectSample } from "./samples";

let onUnauthorizedCallback: (() => void) | null = null;

export const setOnUnauthorized = (callback: () => void) => {
  onUnauthorizedCallback = callback;
};

export const createApiClient = (token: string | null) => {
  const API_URL = import.meta.env.VITE_API_URL ? import.meta.env.VITE_API_URL : `${window.location.origin}/api`;
  const client = axios.create({
    baseURL: API_URL,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  client.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      if (error.response?.status === 401 && onUnauthorizedCallback) {
        onUnauthorizedCallback();
      }
      return Promise.reject(error);
    },
  );

  return client;
};

export const getData = async <T>(token: string | null, url: string, params?: AxiosRequestConfig): Promise<T> => {
  const client = createApiClient(token);
  const response = await client.get(url, params);
  return response.data;
};

export const postData = async <T>(token: string | null, url: string, data?: unknown): Promise<T> => {
  const client = createApiClient(token);
  const response = await client.post(url, data);
  return response.data;
};

export const putData = async <T>(
  token: string | null,
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> => {
  const client = createApiClient(token);
  const response = await client.put(url, data, config);
  return response.data;
};

export const deleteData = async (token: string | null, url: string): Promise<void> => {
  const client = createApiClient(token);
  await client.delete(url);
};

export type DataVersionCheck<T = unknown> =
  | { kind: "missing" }
  | { kind: "unchanged" }
  | { kind: "changed"; etag: string | null; lastModified: string | null; data: T };

export const checkDataVersion = async <T = unknown>(
  token: string | null,
  url: string,
  etag: string | null,
): Promise<DataVersionCheck<T>> => {
  const client = createApiClient(token);
  try {
    const response = await client.get(url, {
      headers: etag ? { "If-None-Match": etag } : undefined,
      validateStatus: (status) => (status >= 200 && status < 300) || status === 304,
    });
    if (response.status === 304) {
      return { kind: "unchanged" };
    }
    const headers = response.headers as AxiosResponseHeaders;
    const lastModifiedHeader = headers["last-modified"];
    const lastModified = typeof lastModifiedHeader === "string" ? lastModifiedHeader : null;
    const etagHeader = headers.etag;
    return {
      kind: "changed",
      etag: typeof etagHeader === "string" ? etagHeader : null,
      lastModified,
      data: response.data,
    };
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return { kind: "missing" };
    }
    throw error;
  }
};

export const headData = async <T>(
  token: string | null,
  url: string,
  mapFunc: (headers: AxiosResponseHeaders) => T,
  config?: AxiosRequestConfig,
): Promise<T> => {
  const client = createApiClient(token);
  const response = await client.head(url, config);

  const headers = response.headers as AxiosResponseHeaders;
  return mapFunc(headers);
};

export type SortOption = "name" | "number" | "client";

export interface ProjectData {
  id: string;
  name: string;
  number?: string;
  client: string;
  contract?: string;
  status_counts: Record<string, number>;
}

export const apiClient = {
  getFromUrl: (token: string | null, url: string): Promise<unknown> => getData(token, url),

  findExternalProjects: async (token: string | null, query: string): Promise<Project[]> => {
    return await getData(token, "/external/projects", { params: { query } });
  },

  getProject: async (token: string | null, projectId: string): Promise<Project> => {
    return await getData(token, `/projects/${projectId}`);
  },

  listProjects: async (token: string | null, searchName: string): Promise<Project[]> => {
    return await getData(token, "/projects/", { params: { search: searchName } });
  },

  listSamplesAtLocation: async (
    token: string | null,
    projectGoldenRecordId: string,
    location: string,
  ): Promise<ProjectSample[]> => {
    return await getData(token, "/samples/", { params: { projectGoldenRecordId, location } });
  },

  listAllProjectSamples: async (token: string | null, projectGoldenRecordId: string): Promise<ProjectSample[]> => {
    return await getData(token, "/samples/all", { params: { projectGoldenRecordId } });
  },

  listProjectLocations: async (token: string | null, projectGoldenRecordId: string): Promise<string[]> => {
    return await getData(token, "/samples/locations", { params: { projectGoldenRecordId } });
  },

  listLaboratories: async (token: string | null): Promise<{ id: string; name: string }[]> => {
    return await getData(token, "/laboratories/");
  },

  getLaboratory: async (token: string | null, id: string): Promise<Laboratory> => {
    return await getData(token, `/laboratories/${id}`);
  },

  updateSieveStackSieves: async (
    token: string | null,
    laboratoryId: string,
    stackName: string,
    sieves: string[],
  ): Promise<void> => {
    await putData(token, `/laboratories/${laboratoryId}/sieve-stack-sieves`, { stackName, sieves });
  },
};
